import { Box, VStack, Text, Button, useToast, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton } from '@chakra-ui/react'
import { useState } from 'react'
import QRCode from 'react-qr-code'
import { ref, set } from 'firebase/database'
import { database } from '@/services/firebase'

interface QRCodeModalProps {
  isOpen: boolean
  onClose: () => void
  matchId: string
  isController?: boolean
}

export function QRCodeModal({ isOpen, onClose, matchId, isController = false }: QRCodeModalProps) {
  const [showQRCode, setShowQRCode] = useState(false)
  const [controllerToken, setControllerToken] = useState<string | null>(null)
  const [shareType, setShareType] = useState<'display' | 'controller' | null>(null)
  const toast = useToast()

  const generateControllerToken = async () => {
    try {
      const newToken = crypto.randomUUID()
      const controllerRef = ref(database, `matches/${matchId}/controllers/${newToken}`)
      
      await set(controllerRef, {
        createdAt: Date.now(),
        expiresAt: Date.now() + (24 * 60 * 60 * 1000), // 24 hours
        isActive: true,
        permissions: ['score', 'control']
      })

      return newToken
    } catch (error) {
      console.error('Token generation error:', error)
      throw error
    }
  }

  const handleDisplayOnly = () => {
    setShareType('display')
    setShowQRCode(true)
  }

  const handleControllerAccess = async () => {
    try {
      const newToken = await generateControllerToken()
      setControllerToken(newToken)
      setShareType('controller')
      setShowQRCode(true)
    } catch (error) {
      console.error('Error generating controller token:', error)
      toast({
        title: 'Error',
        description: 'Failed to generate controller access. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const handleCastController = async () => {
    try {
      const newToken = await generateControllerToken()
      setControllerToken(newToken)
      setShareType('controller')
      setShowQRCode(true)
    } catch (error) {
      console.error('Error generating controller token:', error)
      toast({
        title: 'Error',
        description: 'Failed to generate controller access. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const handleClose = () => {
    setShowQRCode(false)
    setControllerToken(null)
    onClose()
  }

  const getDisplayUrl = () => {
    const url = new URL(window.location.origin)
    url.searchParams.set('matchId', matchId)
    return url.toString()
  }

  const getControllerUrl = () => {
    if (!controllerToken) return ''
    const url = new URL(window.location.origin)
    url.searchParams.set('matchId', matchId)
    url.searchParams.set('token', controllerToken)
    url.searchParams.set('controller', 'true')
    return url.toString()
  }

  const handleShare = async () => {
    const shareUrl = shareType === 'controller' ? getControllerUrl() : getDisplayUrl()
    const shareTitle = shareType === 'controller' ? 'Match Controller Access' : 'Match Display Access'
    const shareText = shareType === 'controller' ? 'Join this match as a controller' : 'Join this match as display only'
    
    try {
      if (navigator.share) {
        console.log('Using native share...')
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl
        })
      } else {
        console.log('Using clipboard fallback...')
        await navigator.clipboard.writeText(shareUrl)
        toast({
          title: `${shareType === 'controller' ? 'Controller' : 'Display'} link copied to clipboard`,
          status: 'success',
          duration: 2000,
        })
      }
    } catch (error: any) {
      console.error('Share error:', error)
      if (error.name !== 'AbortError') {
        toast({
          title: `Failed to share ${shareType === 'controller' ? 'controller' : 'display'} link`,
          status: 'error',
          duration: 2000,
        })
      }
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size={{ base: "full", md: "md" }} closeOnOverlayClick={true}>
      <ModalOverlay />
      <ModalContent bg="#09090b" color="white" mx={4}>
        <ModalHeader>Connect Devices</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!showQRCode ? (
            <VStack spacing={4} align="center" pb={6}>
              <Button
                colorScheme="blue"
                onClick={handleDisplayOnly}
                w="100%"
                size="lg"
                bg="#191919"
                _hover={{ bg: '#2a2a2a' }}
                color="white"
                fontWeight="normal"
                border="1px solid"
                borderColor="gray.600"
              >
                Share Display Only
              </Button>
              {isController && (
                <Button
                  colorScheme="green"
                  onClick={handleControllerAccess}
                  w="100%"
                  size="lg"
                  bg="#191919"
                  _hover={{ bg: '#2a2a2a' }}
                  color="white"
                  fontWeight="normal"
                  border="1px solid"
                  borderColor="gray.600"
                >
                  Share Controller Access
                </Button>
              )}
              {/* {isController && (
                <Button
                  colorScheme="green"
                  onClick={handleCastController}
                  w="100%"
                  size="lg"
                  bg="#191919"
                  _hover={{ bg: '#2a2a2a' }}
                  color="white"
                  fontWeight="normal"
                  border="1px solid"
                  borderColor="gray.600"
                >
                  Cast Controller (Wifi)
                </Button>
              )} */}
            </VStack>
          ) : (
            <VStack spacing={4} align="center" pb={6}>
              <Box p={4} bg="white" borderRadius="lg" boxShadow="md" w="100%" maxW={{ base: "280px", md: "300px" }} mx="auto">
                <QRCode
                  value={shareType === 'controller' ? getControllerUrl() : getDisplayUrl()}
                  size={200}
                  style={{ height: 'auto', maxWidth: '100%', width: '100%' }}
                />
              </Box>

              <Text fontSize="sm" color="gray.400" textAlign="center">
                {shareType === 'controller' 
                  ? 'Scan this QR code to connect as a controller. The QR code will grant full control functionality.'
                  : 'Scan this QR code to connect as display only. This will show the match score without control functionality.'
                }
              </Text>

              <Box
                p={2}
                bg="gray.800"
                borderRadius="md"
                w="100%"
                textAlign="center"
                fontSize="sm"
                color="gray.300"
              >
                {shareType === 'controller' ? getControllerUrl() : getDisplayUrl()}
              </Box>

              <Button
                colorScheme="blue"
                onClick={handleShare}
                w="100%"
                size="lg"
                bg="#191919"
                _hover={{ bg: '#2a2a2a' }}
                color="white"
                fontWeight="normal"
                border="1px solid"
                borderColor="gray.600"
              >
                Share {shareType === 'controller' ? 'Controller' : 'Display'} Link
              </Button>
              
              <Button
                colorScheme="gray"
                onClick={handleClose}
                w="100%"
                size="lg"
                bg="#191919"
                _hover={{ bg: '#2a2a2a' }}
                color="white"
                fontWeight="normal"
                border="1px solid"
                borderColor="gray.600"
              >
                Close
              </Button>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
} 